import { BulbOutlined, CopyOutlined, LogoutOutlined, SwapOutlined } from "@ant-design/icons";
import { type Language } from "@ap/i18n";
import { useAuth } from "@ap/ui-kit";
import { App, Dropdown, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { runtimeConfig } from "../config";
import { PLATFORM_TENANT, tenantLabel } from "../features/admin/UsersTab";
import { roleLabel } from "../lib/roleLabel";

/** Roles that act on what every organisation shares (models, tools, settings, the global stop). */
const PLATFORM_ROLES = ["platform-admin", "platform-auditor", "tool-admin", "marketplace-curator", "model-admin"];

const SWITCH_PREFIX = "switch:";

export function initials(name: string | undefined): string {
  const parts = (name ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toLocaleUpperCase();
}

/**
 * Account menu (ADR-0052/0055): user ID (copy), Switch user (local demo accounts), Sign out.
 * The button's accessible name stays "Account menu"; the visible name is kept in its text.
 */
export function AccountMenu({ lang, compact }: { lang: Language; compact?: boolean }) {
  const { t } = useTranslation("shell");
  const { user, logout, switchUser } = useAuth();
  const demoUsers = switchUser ? runtimeConfig().demoUsers : [];
  const { message } = App.useApp();
  const navigate = useNavigate();
  const tm = useTranslation("myMemory").t;
  const ta = useTranslation("admin").t;
  const platformRoles = user?.tenant === PLATFORM_TENANT ? PLATFORM_ROLES.filter((r) => user.roles?.includes(r)) : [];
  // Platform team members see what their platform roles mean (they act for every organisation).
  const scope = platformRoles.length
    ? [
        {
          key: "scope",
          label: (
            <span data-testid="platform-scope" style={{ display: "block", maxInlineSize: 320, whiteSpace: "normal" }}>
              <Typography.Text strong>
                {t("platformScope.title", { org: tenantLabel(t, PLATFORM_TENANT), roles: platformRoles.map((r) => roleLabel(ta, r)).join(t("forbidden.sep")) })}
              </Typography.Text>
              <br />
              <Typography.Text type="secondary">{t("platformScope.note")}</Typography.Text>
            </span>
          ),
        },
        { type: "divider" as const },
      ]
    : [];
  return (
    <Dropdown
      placement={lang === "ar" ? "topRight" : "topLeft"}
      menu={{
        items: [
          ...scope,
          // Data-subject requests: users give the privacy officer this ID (the token's `sub`).
          { key: "uid", icon: <CopyOutlined />, label: <span data-testid="user-id">{t("userId", { id: user?.sub ?? "" })}</span>, title: t("copyUserId") },
          { key: "memory", icon: <BulbOutlined />, label: <span data-testid="my-memory">{tm("menu")}</span> },
          { type: "divider" as const },
          // Local stack only: sign in again as another demo account (each has other roles).
          ...(demoUsers.length > 0
            ? [
                {
                  key: "switch",
                  icon: <SwapOutlined />,
                  label: t("switchUser"),
                  children: demoUsers.map((d) => ({
                    key: `${SWITCH_PREFIX}${d.username}`,
                    disabled: d.username === user?.username,
                    label: (
                      <span data-testid={`switch-user-${d.username}`}>
                        <Typography.Text strong>{d.username}</Typography.Text>
                        {d.description ? <Typography.Text type="secondary"> — {d.description[lang]}</Typography.Text> : null}
                      </span>
                    ),
                  })),
                },
                { type: "divider" as const },
              ]
            : []),
          { key: "logout", icon: <LogoutOutlined />, label: t("logout") },
        ],
        onClick: ({ key }) => {
          if (key === "logout") void logout();
          if (key === "memory") void navigate("/me/memory");
          if (key.startsWith(SWITCH_PREFIX) && switchUser) void switchUser(key.slice(SWITCH_PREFIX.length));
          if (key === "uid" && user?.sub) {
            void navigator.clipboard?.writeText(user.sub).then(
              () => message.success(t("copied")),
              () => undefined,
            );
          }
        },
      }}
    >
      <button type="button" className="ap-account" aria-label={t("userMenu")} data-compact={compact ? "true" : undefined} data-tour="account">
        <span className="ap-avatar" aria-hidden>
          {initials(user?.name)}
        </span>
        <span className={compact ? "ap-visually-hidden" : "ap-account-text"}>
          <span className="ap-account-name">{user?.name}</span>
          {user?.tenant && !compact ? <span className="ap-account-sub">{user.username}</span> : null}
        </span>
      </button>
    </Dropdown>
  );
}
